import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Box,
  CssBaseline,
  TextField,
  Button,
  Modal,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  EditNote,
  DeleteForever,
  Search,
  Add,
  Cancel,
  DescriptionOutlined,
} from '@mui/icons-material';
import axios from 'axios';
import { global_link } from '../Components/Globallink';
import modalStyle from '../Components/ModalStyle';

// Match the surface used on script cards / rest of the app
const surfaceSx = {
  bgcolor: 'background.paper',
  boxShadow: 'none',
  border: '0.1px solid rgba(207, 211, 212, 0.2)',
  borderRadius: '5px',
};

const Scripts = () => {
  const api = axios.create({ baseURL: global_link });
  const authToken = process.env.AUTH_TOKEN;

  const [searchTerm, setSearchTerm] = useState('');
  const [scriptsData, setScriptsData] = useState([]);
  const [openAddScriptModal, setOpenAddScriptModal] = useState(false);
  const [openEditScriptModal, setOpenEditScriptModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const isSupervisor = sessionStorage.getItem('user_level') === 'supervisor';

  const fetchScripts = async () => {
    try {
      const authyToken = localStorage.getItem(authToken);
      const response = await api.get('/script/getscripts', {
        params: { search: searchTerm },
        headers: { Authorization: `Bearer ${authyToken}` },
      });
      setScriptsData(response.data || []);
    } catch (error) {
      console.error('Error fetching Scripts:', error);
    }
  };

  useEffect(() => {
    fetchScripts();
  }, [searchTerm]);

  const handleScriptSubmit = (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const authyToken = localStorage.getItem(authToken);
    api
      .post('/script/add', formData, {
        headers: {
          Authorization: `Bearer ${authyToken}`,
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      })
      .then(() => {
        setOpenAddScriptModal(false);
        fetchScripts();
      })
      .catch((error) => console.error(error));
  };

  const handleScriptEditSubmit = (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const authyToken = localStorage.getItem(authToken);
    api
      .put('/script/update/', formData, {
        headers: {
          Authorization: `Bearer ${authyToken}`,
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      })
      .then(() => {
        fetchScripts();
        setOpenEditScriptModal(false);
      })
      .catch((error) => console.error(error));
  };

  const handleScriptDelete = (id) => {
    if (!window.confirm('Delete this script?')) return;
    const authyToken = localStorage.getItem(authToken);
    api
      .delete(`/script/delete/${id}`, {
        headers: {
          Authorization: `Bearer ${authyToken}`,
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      })
      .then(() => {
        fetchScripts();
        setOpenEditScriptModal(false);
      })
      .catch((error) => console.error(error));
  };

  const handleInputModChange = (event) => {
    const { name, value } = event.target;
    setSelectedItem((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <>
      <CssBaseline />

      {/* Header — same rhythm as your other pages */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5">Scripts</Typography>
          <Typography variant="body2" color="text.secondary">
            {scriptsData.length} script{scriptsData.length === 1 ? '' : 's'}
            {searchTerm ? ` matching “${searchTerm}”` : ''}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
          <TextField
            size="small"
            placeholder="Search scripts…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: { sm: 240 }, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          {isSupervisor && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => setOpenAddScriptModal(true)}
              sx={{ padding: '10px', whiteSpace: 'nowrap' }}
            >
              Add Script
            </Button>
          )}
        </Stack>
      </Box>

      {/* Cards */}
      {scriptsData.length === 0 ? (
        <Box
          sx={{
            ...surfaceSx,
            py: 8,
            px: 3,
            textAlign: 'center',
            borderStyle: 'dashed',
          }}
        >
          <DescriptionOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            No scripts found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm
              ? 'Try a different search term.'
              : 'Create your first call script or reminder.'}
          </Typography>
          {isSupervisor && !searchTerm && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => setOpenAddScriptModal(true)}
            >
              Add Script
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={1.7}>
          {scriptsData.map((row) => {
            const isExpanded = expandedId === row.id;
            return (
              <Grid item xs={12} sm={6} md={4} key={row.id}>
                <Box
                  sx={{
                    ...surfaceSx,
                    height: '100%',
                    p: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'border-color 0.15s ease',
                    '&:hover': {
                      borderColor: 'rgba(207, 211, 212, 0.55)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Chip
                      label={row.department || 'General'}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{ fontWeight: 500 }}
                    />
                    {isSupervisor && (
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => {
                            setSelectedItem(row);
                            setOpenEditScriptModal(true);
                          }}
                        >
                          <EditNote fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  <Divider sx={{ my: 1.2, borderColor: 'rgba(207, 211, 212, 0.25)' }} />

                  <Typography variant="h6" sx={{ fontSize: '1.05rem', mb: 1 }}>
                    {row.header}
                  </Typography>

                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontSize: '15px',
                      fontWeight: 400,
                      flex: 1,
                      ...(isExpanded
                        ? {}
                        : {
                            display: '-webkit-box',
                            WebkitLineClamp: 5,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }),
                    }}
                  >
                    {row.content}
                  </Typography>

                  {row.content && String(row.content).length > 180 && (
                    <Button
                      size="small"
                      onClick={() => setExpandedId(isExpanded ? null : row.id)}
                      sx={{ mt: 1, alignSelf: 'flex-start', px: 0 }}
                    >
                      {isExpanded ? 'Show less' : 'Read more'}
                    </Button>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Add modal — uses shared modalStyle (theme-matched) */}
      <Modal
        open={openAddScriptModal}
        onClose={() => setOpenAddScriptModal(false)}
        aria-labelledby="add-script-title"
      >
        <Box sx={modalStyle}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography id="add-script-title" variant="h6" component="h3">
              Add Script / Reminder
            </Typography>
            <Cancel
              onClick={() => setOpenAddScriptModal(false)}
              sx={{ cursor: 'pointer' }}
            />
          </Box>

          <Divider sx={{ my: 1.5, borderColor: 'rgba(207, 211, 212, 0.25)' }} />

          <form onSubmit={handleScriptSubmit}>
            <TextField
              label="Department"
              placeholder="Department"
              variant="outlined"
              fullWidth
              name="department"
              sx={{ mt: 1.5 }}
            />
            <TextField
              label="Header"
              placeholder="Header"
              variant="outlined"
              fullWidth
              name="header"
              sx={{ mt: 1.5 }}
            />
            <TextField
              multiline
              rows={6}
              label="Content"
              placeholder="Write the script agents should follow…"
              variant="outlined"
              fullWidth
              name="content"
              required
              sx={{ mt: 1.5 }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ mt: 1.5, py: 1.2 }}
            >
              Submit Script
            </Button>
          </form>
        </Box>
      </Modal>

      {/* Edit modal — same modalStyle as Add / rest of app */}
      <Modal
        open={openEditScriptModal}
        onClose={() => setOpenEditScriptModal(false)}
        aria-labelledby="edit-script-title"
      >
        <Box sx={modalStyle}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography id="edit-script-title" variant="h6" component="h3">
              Edit Script
            </Typography>
            <Cancel
              onClick={() => setOpenEditScriptModal(false)}
              sx={{ cursor: 'pointer' }}
            />
          </Box>

          <Divider sx={{ my: 1.5, borderColor: 'rgba(207, 211, 212, 0.25)' }} />

          {selectedItem && (
            <form onSubmit={handleScriptEditSubmit}>
              <TextField
                label="Department"
                placeholder="Department"
                variant="outlined"
                fullWidth
                name="department"
                value={selectedItem.department || ''}
                onChange={handleInputModChange}
                sx={{ mt: 1.5 }}
              />
              <TextField
                name="id"
                value={selectedItem.id || ''}
                sx={{ display: 'none' }}
              />
              <TextField
                label="Header"
                placeholder="Header"
                variant="outlined"
                fullWidth
                name="header"
                value={selectedItem.header || ''}
                onChange={handleInputModChange}
                sx={{ mt: 1.5 }}
              />
              <TextField
                multiline
                rows={6}
                label="Content"
                placeholder="Content"
                variant="outlined"
                fullWidth
                name="content"
                value={selectedItem.content || ''}
                onChange={handleInputModChange}
                required
                sx={{ mt: 1.5 }}
              />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  mt: 1.5,
                }}
              >
                <Button
                  type="button"
                  variant="contained"
                  color="error"
                  startIcon={<DeleteForever />}
                  onClick={() => handleScriptDelete(selectedItem.id)}
                  sx={{ py: 1.2 }}
                >
                  Delete
                </Button>
                <Button
                  type="submit"
                  variant="outlined"
                  color="warning"
                  sx={{ py: 1.2 }}
                >
                  Save Changes
                </Button>
              </Box>
            </form>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default Scripts;
